// Considered: SpherePerspectiveRow — rejected because it edits one perspective's assignees and cannot
// express a ledger-backed statement with stance, a guarded parent change or a link list. See spec Reuse Audit.
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { PurposeAlsoServesEntry, PurposeStatement, SpherePurpose } from 'types/purpose';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import StatusBadgeAtom, { type StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import './PurposeFieldMolecule.scss';

const PURPOSE_STATEMENT_MAX = 500;
/** The counter appears only when the limit is near, so it is not noise while typing a short line. */
const COUNTER_THRESHOLD = 450;

export type PurposeFieldStatus = 'ready' | 'loading' | 'error';

export interface PurposeFieldMoleculeProps {
  status: PurposeFieldStatus;
  /** The server's purpose read; ignored unless `status` is `ready`. */
  purpose: SpherePurpose | null;
  /** Why the statement is read-only when the server says the caller cannot author it. */
  readOnlyReason?: 'person' | 'sphere';
  /** The person the statement belongs to, named in the read-only reason. */
  personName?: string;
  saving?: boolean;
  affirming?: boolean;
  /** A message for the last failed save or affirm; the typed text is kept. */
  error?: string | null;
  /** The revision moved while the user was typing: the latest is shown and their text kept below. */
  conflict?: boolean;
  /** `text` is `null` when the user cleared an existing statement. */
  onSave?: (text: string | null) => void;
  onAffirm?: () => void;
  onChangeParent?: () => void;
  onAddLink?: () => void;
  onRemoveLink?: (entry: PurposeAlsoServesEntry) => void;
  onRetry?: () => void;
}

/** `Date#toLocaleDateString` result, or `false` when there's no `affirmedAt` to format, or
 * `undefined` when there's no statement at all. */
type AffirmedDate = string | false | undefined;

const stanceTone = (statement: PurposeStatement | null): StatusBadgeTone => (statement?.stance === 'affirmed' ? 'success' : 'neutral');

const stanceLabel = (statement: PurposeStatement, affirmedDate: AffirmedDate, t: (key: string, opts?: Record<string, unknown>) => string): string =>
  statement.stance === 'affirmed' ? t('purpose.field.stanceAffirmed', { date: affirmedDate || '' }).trim() : t('purpose.field.stanceDraft');

interface StanceBadgeProps {
  statement: PurposeStatement;
  affirmedDate: AffirmedDate;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const StanceBadge: React.FC<StanceBadgeProps> = ({ statement, affirmedDate, t }) => (
  <StatusBadgeAtom tone={stanceTone(statement)} label={stanceLabel(statement, affirmedDate, t)} data-testid="purpose-stance" />
);

interface StatementFormProps {
  text: string;
  setText: (value: string) => void;
  setTouched: () => void;
  shownValidation: string | undefined;
  canSave: boolean;
  saving: boolean;
  error?: string | null;
  conflict: boolean;
  statement: PurposeStatement | null;
  affirmedDate: AffirmedDate;
  showAffirm: boolean;
  affirming: boolean;
  onAffirm?: () => void;
  onSubmit: (event: React.SyntheticEvent) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const StatementForm: React.FC<StatementFormProps> = ({
  text,
  setText,
  setTouched,
  shownValidation,
  canSave,
  saving,
  error,
  conflict,
  statement,
  affirmedDate,
  showAffirm,
  affirming,
  onAffirm,
  onSubmit,
  t,
}) => {
  const helperText = text.length >= COUNTER_THRESHOLD ? t('purpose.field.counter', { count: text.length, max: PURPOSE_STATEMENT_MAX }) : undefined;
  return (
    <form className="purpose-field__statement" onSubmit={onSubmit} noValidate>
      <TextareaAtom
        label={conflict ? t('purpose.field.yourText') : t('purpose.field.statementLabel')}
        placeholder={t('purpose.field.statementPlaceholder')}
        value={text}
        rows={3}
        autoResize
        onChange={(event) => setText(event.target.value)}
        autoComplete="off"
        onBlur={setTouched}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) onSubmit(event);
        }}
        error={!!shownValidation}
        errorMessage={shownValidation}
        helperText={helperText}
      />
      {error && (
        <p className="purpose-field__error" role="alert">
          {error}
        </p>
      )}
      <div className="purpose-field__actions">
        {statement && <StanceBadge statement={statement} affirmedDate={affirmedDate} t={t} />}
        <ButtonAtom type="submit" size="small" label={saving ? t('purpose.field.saving') : t('purpose.field.save')} disabled={!canSave} />
        {showAffirm && (
          <ButtonAtom type="button" variant="secondary" size="small" label={t('purpose.field.affirm')} loading={affirming} onClick={onAffirm} />
        )}
      </div>
    </form>
  );
};

interface StatementReadOnlyProps {
  statement: PurposeStatement | null;
  affirmedDate: AffirmedDate;
  readOnlyText: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const StatementReadOnly: React.FC<StatementReadOnlyProps> = ({ statement, affirmedDate, readOnlyText, t }) => (
  <div className="purpose-field__statement purpose-field__statement--read-only">
    {statement ? (
      <>
        <p className="purpose-field__text">{statement.text}</p>
        <StanceBadge statement={statement} affirmedDate={affirmedDate} t={t} />
      </>
    ) : (
      <p className="purpose-field__empty">{t('purpose.field.empty')}</p>
    )}
    <p className="purpose-field__reason">{readOnlyText}</p>
  </div>
);

interface AlsoServesSectionProps {
  alsoServes: PurposeAlsoServesEntry[];
  removable: boolean;
  showAdd: boolean;
  onAddLink?: () => void;
  onRemoveLink?: (entry: PurposeAlsoServesEntry) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const AlsoServesSection: React.FC<AlsoServesSectionProps> = ({ alsoServes, removable, showAdd, onAddLink, onRemoveLink, t }) => (
  <div className="purpose-field__row purpose-field__row--links" data-datum="purpose-also-serves">
    <span className="purpose-field__row-label">{t('purpose.field.alsoServes')}</span>
    <ul className="purpose-field__chips">
      {alsoServes.map((entry) => (
        // ChipAtom's own remove control (forced to a real <button> via removeDatum below) is the
        // sole focusable, interactive element, and it removes on click and on Enter/Space like any
        // button. The <li> carries no listener: it is not focusable, so a delegated Delete/Backspace
        // handler here could only ever fire while that button already had focus — a second path to
        // an action the button already performs, on a non-interactive element (S6847).
        <li key={entry.relationshipId}>
          <ChipAtom
            label={entry.sphere.name}
            removable={removable}
            removeDatum={removable ? entry.relationshipId : undefined}
            removeAriaLabel={removable ? t('purpose.field.alsoServesRemove', { name: entry.sphere.name }) : undefined}
            onRemove={() => {
              onRemoveLink?.(entry);
              return false;
            }}
          />
        </li>
      ))}
    </ul>
    {showAdd && <ButtonAtom variant="text" size="small" label={t('purpose.field.alsoServesAdd')} onClick={onAddLink} />}
  </div>
);

interface IndirectlyServesSectionProps {
  indirectlyServes: SpherePurpose['indirectlyServes'];
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/** Owns its own disclosure state — purposes served through another sphere are consequences of the
 * parent chain, not decisions made here, so they stay behind a toggle (spec XP3). */
const IndirectlyServesSection: React.FC<IndirectlyServesSectionProps> = ({ indirectlyServes, t }) => {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  return (
    <div className="purpose-field__row purpose-field__row--indirect" data-datum="purpose-indirectly-serves">
      <ButtonAtom
        variant="text"
        size="small"
        className="purpose-field__indirect-toggle"
        label={expanded ? t('purpose.field.indirectHide') : t('purpose.field.indirectShowAll', { count: indirectlyServes.length })}
        aria-label={expanded ? t('purpose.field.indirectHideAria') : t('purpose.field.indirectShowAllAria', { count: indirectlyServes.length })}
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={() => setExpanded((open) => !open)}
      />
      {expanded && (
        <ul className="purpose-field__indirect" id={listId}>
          {indirectlyServes.map((entry) => (
            <li key={`${entry.through.id}:${entry.sphere.id}`}>
              {entry.sphere.name} · {t('purpose.field.indirectlyServes', { through: entry.through.name })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const parentTextFor = (purpose: SpherePurpose, t: (key: string, opts?: Record<string, unknown>) => string): string => {
  const parent = purpose.source === 'parent' ? purpose.path[0] : undefined;
  if (parent) return parent.name;
  if (purpose.source === 'unplaced') return t('purpose.line.unplaced');
  return t('purpose.field.parentNone');
};

const readOnlyTextFor = (
  readOnlyReason: 'person' | 'sphere',
  personName: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  if (readOnlyReason !== 'person') return t('purpose.field.readOnlySphere');
  if (personName) return t('purpose.field.readOnlyPerson', { person: personName });
  return t('purpose.field.readOnlyPersonUnknown');
};

const validationFor = (tooLong: boolean, whitespaceOnly: boolean, t: (key: string, opts?: Record<string, unknown>) => string): string | undefined => {
  if (tooLong) return t('purpose.field.validation.tooLong', { max: PURPOSE_STATEMENT_MAX });
  if (whitespaceOnly) return t('purpose.field.validation.required');
  return undefined;
};

const PurposeFieldMolecule: React.FC<PurposeFieldMoleculeProps> = ({
  status,
  purpose,
  readOnlyReason = 'sphere',
  personName,
  saving = false,
  affirming = false,
  error,
  conflict = false,
  onSave,
  onAffirm,
  onChangeParent,
  onAddLink,
  onRemoveLink,
  onRetry,
}) => {
  const { t, i18n } = useTranslation();
  const saved = purpose?.statement?.text ?? '';
  const [text, setText] = useState(saved);
  const [touched, setTouched] = useState(false);
  const dirty = text !== saved;

  // Adopt a newer server value only while the user has nothing unsaved, so a reload after a
  // conflict never overwrites what they typed.
  const previousSaved = useRef(saved);
  useEffect(() => {
    const previous = previousSaved.current;
    previousSaved.current = saved;
    setText((current) => (current === previous ? saved : current));
  }, [saved]);

  if (status === 'loading') {
    return (
      <output className="purpose-field purpose-field--loading" aria-label={t('purpose.field.loading')}>
        <SkeletonAtom count={3} height="1.25rem" gap="var(--spacing-sm)" />
      </output>
    );
  }

  if (status === 'error' || !purpose) {
    return (
      <section className="purpose-field purpose-field--error" role="alert">
        <p className="purpose-field__error-title">{t('purpose.field.loadError')}</p>
        <p className="purpose-field__error-reason">{t('purpose.field.loadErrorReason')}</p>
        {onRetry && (
          <ButtonAtom variant="secondary" size="small" label={t('purpose.field.loadErrorAction')} onClick={onRetry} />
        )}
      </section>
    );
  }

  const { capabilities } = purpose;
  const canAuthor = capabilities.authorStatement;
  const trimmed = text.trim();
  const tooLong = text.length > PURPOSE_STATEMENT_MAX;
  const whitespaceOnly = text.length > 0 && trimmed.length === 0;
  const validation = validationFor(tooLong, whitespaceOnly, t);
  const canSave = dirty && !validation && !saving;
  const shownValidation = tooLong || touched ? validation : undefined;

  const statement = purpose.statement;
  const showAffirm = canAuthor && !!statement && statement.stance === 'draft' && !dirty && !!onAffirm;
  const parentText = parentTextFor(purpose, t);
  const readOnlyText = readOnlyTextFor(readOnlyReason, personName, t);

  const affirmedDate =
    statement?.affirmedAt &&
    new Date(statement.affirmedAt).toLocaleDateString(i18n?.language, { dateStyle: 'medium' });

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (canSave) onSave?.(trimmed.length === 0 ? null : trimmed);
  };

  const linksRemovable = capabilities.manageLinks && !!onRemoveLink;
  const showAlsoServes = purpose.alsoServes.length > 0 || (capabilities.manageLinks && !!onAddLink);

  return (
    <section className="purpose-field" data-state={canAuthor ? 'editable' : 'read-only'} aria-labelledby="purpose-field-heading">
      <h3 id="purpose-field-heading" className="purpose-field__heading">
        {t('purpose.field.section')}
      </h3>
      <p className="purpose-field__hint">{t('purpose.field.hint')}</p>

      {conflict && (
        <p className="purpose-field__conflict" role="alert">
          {t('purpose.field.conflict')}
        </p>
      )}

      {canAuthor ? (
        <StatementForm
          text={text}
          setText={setText}
          setTouched={() => setTouched(true)}
          shownValidation={shownValidation}
          canSave={canSave}
          saving={saving}
          error={error}
          conflict={conflict}
          statement={statement}
          affirmedDate={affirmedDate}
          showAffirm={showAffirm}
          affirming={affirming}
          onAffirm={onAffirm}
          onSubmit={submit}
          t={t}
        />
      ) : (
        <StatementReadOnly statement={statement} affirmedDate={affirmedDate} readOnlyText={readOnlyText} t={t} />
      )}

      <div className="purpose-field__row" data-datum="purpose-parent">
        <span className="purpose-field__row-label">{t('purpose.field.parentLabel')}</span>
        <span className="purpose-field__row-value">{parentText}</span>
        {capabilities.changeParent && onChangeParent && (
          <ButtonAtom variant="text" size="small" label={t('purpose.field.change')} onClick={onChangeParent} />
        )}
      </div>

      {showAlsoServes && (
        <AlsoServesSection
          alsoServes={purpose.alsoServes}
          removable={linksRemovable}
          showAdd={!!(capabilities.manageLinks && onAddLink)}
          onAddLink={onAddLink}
          onRemoveLink={onRemoveLink}
          t={t}
        />
      )}

      {purpose.indirectlyServes.length > 0 && <IndirectlyServesSection indirectlyServes={purpose.indirectlyServes} t={t} />}
    </section>
  );
};

export default PurposeFieldMolecule;
